import { useState, useEffect } from 'react';

import { useRouter } from 'next/router';

import {
  useMediaQuery,
  colors,
  Grid,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { LoadingButton } from '@material-ui/lab';

import PropTypes from 'prop-types';

import { Image, Icon } from '@/src/components/atoms';
import { SectionHeader } from '@/src/components/molecules';

const useStyles = makeStyles(theme => ({
  image: {
    width: 400,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 400,
    },
  },
  searchInputContainer: {
    background: theme.palette.alternate.main,
    padding: theme.spacing(2),
    boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.11)',
    borderRadius: theme.spacing(1),
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    '& .MuiOutlinedInput-notchedOutline': {
      border: '0 !important',
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: theme.spacing(2),
    },
    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: 0,
    },
    '& .MuiOutlinedInput-input': {
      padding: 0,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  searchButton: {
    maxHeight: 45,
    minWidth: 135,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
}));

const Hero = props => {
  const { className, loading, ...rest } = props;

  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  const { search = '' } = router.query;

  const [input, setInput] = useState(search);

  useEffect(() => {
    setInput(search);
  }, [search]);

  const handleFind = () =>
    router.push({ pathname: '/success-cases', query: { search: input } }, undefined, {
      shallow: true,
      scroll: false,
    });

  return (
    <div className={className} {...rest}>
      <Grid container justifyContent="space-between" spacing={isMd ? 4 : 2}>
        <Grid item xs={12} md={6}>
          <SectionHeader
            title={
              <span>
                Conheça alguns dos nossos{' '}
                <Typography component="span" variant="inherit" color="primary">
                  casos de sucesso
                </Typography>
              </span>
            }
            subtitle="Não entregamos apenas uma tecnologia, oferecemos uma solução que faz a diferença no dia a dia dos nossos parceiros de negócio."
            align={isMd ? 'left' : 'center'}
            disableGutter
            titleVariant="h3"
          />
        </Grid>
        <Grid item container justifyContent="center" xs={12} md={6}>
          <Image src="/images/illustrations/success-cases/hero.svg" className={classes.image} />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.searchInputContainer}>
            <FormControl fullWidth variant="outlined">
              <OutlinedInput
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={ev => {
                  if (ev.key === 'Enter') {
                    handleFind();
                    ev.preventDefault();
                  }
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <Icon fontIconClass="fas fa-search" fontIconColor={colors.blueGrey[900]} />
                  </InputAdornment>
                }
                placeholder="Palavra-chave"
              />
            </FormControl>
            <LoadingButton
              loading={loading}
              loadingPosition="end"
              endIcon={<SearchIcon />}
              color="primary"
              variant="contained"
              size="large"
              onClick={handleFind}
              className={classes.searchButton}>
              Buscar
            </LoadingButton>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

Hero.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Loading state
   */
  loading: PropTypes.bool,
};

export default Hero;
